/**
 * Channels — the rotations you tune the player to.
 *
 * A channel is both the sidebar view and the auto-switch pool: pick Rec and
 * the engine only ever jumps between Rec streamers. Public channels (Top 100
 * and every crew) also carry a persistent shared room, so everyone on the
 * same channel chats together like a watch party while their own player
 * follows the heat. Following and Custom are personal — no room.
 *
 * "Crew" is the mechanism — a curated roster keyed by a server handle, with
 * the constants below as fallback — and it backs two dial rows: the crews
 * proper (Rec, OTK, FaZe, Ninja, AMP) and the Just row of topic channels
 * (Gaming, Politics, IRL, RP), which only differ by where they sit on the dial.
 *
 * The catalogue below is the built-in set. The server's GET /api/channels is
 * the source of truth at runtime: it carries these plus every channel a
 * channel admin created, and mergeServerChannels() turns it into the dial.
 * Every helper takes the live catalogue as its last argument and falls back
 * to the constants, so the built-ins work before the server answers.
 */

export type JustChannelId = string;
export type CrewChannelId = string;
/** 'top' | 'followed' | 'custom' | a roster channel's id (built-in or created) */
export type ChannelId = string;

/** The auto-switch source setting is simply the active channel. */
export type StreamSource = ChannelId;

export interface Channel {
  id: ChannelId;
  /** Chip text and list title */
  label: string;
  /** Persistent shared room id on the server, null for personal sources */
  room: string | null;
  /** custom-lists.json handle the server keeps this crew's roster under */
  handle?: string;
  /** Curated roster used until (or unless) the server roster arrives */
  fallbackStreamers?: string[];
  /** Dial row for crews that aren't crews proper: 'just' = the topic row */
  tag?: 'just';
  /** False for a channel a channel admin created on the server */
  builtin?: boolean;
}

export const CHANNELS: Channel[] = [
  { id: 'top', label: 'Top 100', room: 'top', builtin: true },
  {
    id: 'rec',
    label: 'Rec',
    room: 'rec',
    handle: 'rec',
    builtin: true,
    fallbackStreamers: [
      'quin69', 'summit1g', 'robcdee', 'day9tv', 'anniefuchsia', 'willneff',
      'hasanabi', 'nmplol', 'trainwreckstv', 'zackrawrr', 'nl_kripp', 'forsen',
      'cohhcarnage', 'bikeman', 'itmejp', 'sodapoppin', 'towelliee', 'lirik',
      'xqc', 'maya',
    ],
  },
  {
    id: 'otk',
    label: 'OTK',
    room: 'otk',
    handle: 'otkandfriends',
    builtin: true,
    fallbackStreamers: [
      'asmongold', 'esfandtv', 'mizkif', 'emiru', 'nmplol',
      'richwcampbell', 'tectone', 'otknetwork',
    ],
  },
  {
    id: 'faze',
    label: 'FaZe',
    room: 'faze',
    handle: 'faze',
    builtin: true,
    fallbackStreamers: [
      'fazeclan', 'faze_sway', 'fazeswag', 'nickmercs', 'natehill', 'faze_replays',
    ],
  },
  {
    id: 'ninja',
    label: 'Ninja',
    room: 'ninja',
    handle: 'ninja',
    builtin: true,
    fallbackStreamers: [
      'ninja', 'sypherpk', 'timthetatman', 'couragejd', 'drlupo', 'tfue', 'clix', 'bugha',
    ],
  },
  {
    id: 'amp',
    label: 'AMP',
    room: 'amp',
    handle: 'amp',
    builtin: true,
    fallbackStreamers: [
      'kaicenat', 'fanum', 'agent00gaming', 'imdavisss', 'dukedennis', 'kellhittem',
    ],
  },
  // The Just row — one topic per key. Same machinery as a crew (server
  // handle = id, fallback roster, shared room), just a different rail.
  {
    id: 'gaming',
    label: 'Gaming',
    room: 'gaming',
    handle: 'gaming',
    tag: 'just',
    builtin: true,
    fallbackStreamers: ['summit1g', 'shroud', 'cohhcarnage', 'jynxzi', 'lirik', 'bikeman'],
  },
  {
    id: 'politics',
    label: 'Politics',
    room: 'politics',
    handle: 'politics',
    tag: 'just',
    builtin: true,
    fallbackStreamers: ['hasanabi', 'zackrawrr', 'centralcommittee', 'agendafreetv'],
  },
  {
    id: 'irl',
    label: 'IRL',
    room: 'irl',
    handle: 'irl',
    tag: 'just',
    builtin: true,
    fallbackStreamers: ['robcdee', 'jinnytty', 'jasontheween', 'extraemily'],
  },
  {
    id: 'rp',
    label: 'RP',
    room: 'rp',
    handle: 'rp',
    tag: 'just',
    builtin: true,
    fallbackStreamers: ['timmac'],
  },
  { id: 'followed', label: 'Following', room: null, builtin: true },
  { id: 'custom', label: 'Custom', room: null, builtin: true },
];

export const CHANNEL_IDS: ChannelId[] = CHANNELS.map(c => c.id);

export const CREW_CHANNEL_IDS: CrewChannelId[] = CHANNELS
  .filter(c => !!c.handle)
  .map(c => c.id);

export const JUST_CHANNEL_IDS: JustChannelId[] = CHANNELS
  .filter(c => c.tag === 'just')
  .map(c => c.id);

export const findChannel = (id: unknown, channels: Channel[] = CHANNELS): Channel | undefined =>
  typeof id === 'string' ? channels.find(c => c.id === id) : undefined;

export const isChannelId = (value: unknown, channels: Channel[] = CHANNELS): value is ChannelId =>
  !!findChannel(value, channels);

export const isCrewChannel = (id: ChannelId, channels: Channel[] = CHANNELS): boolean =>
  !!findChannel(id, channels)?.handle;

export const isJustChannel = (id: ChannelId, channels: Channel[] = CHANNELS): boolean =>
  findChannel(id, channels)?.tag === 'just';

/** The channel, or Top 100 for an id the catalogue doesn't know */
export const getChannel = (id: ChannelId, channels: Channel[] = CHANNELS): Channel =>
  findChannel(id, channels) || findChannel('top', channels) || CHANNELS[0];

/** Old settings stored source 'recommended' plus a separate recList key */
const LEGACY_REC_LIST: Record<string, CrewChannelId> = {
  default: 'rec',
  faze: 'faze',
  ninja: 'ninja',
  otkandfriends: 'otk',
  amp: 'amp',
};

/**
 * Coerce whatever was persisted (possibly by an older build) into a channel
 * id. Unknown values land on Top 100.
 */
export const normalizeStreamSource = (
  value: unknown,
  legacyRecList?: unknown,
  channels: Channel[] = CHANNELS,
): ChannelId => {
  if (value === 'recommended') {
    return (typeof legacyRecList === 'string' && LEGACY_REC_LIST[legacyRecList]) || 'rec';
  }
  return isChannelId(value, channels) ? value : 'top';
};

const normalizeLogins = (logins: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  logins.forEach(raw => {
    const login = String(raw).trim().toLowerCase();
    if (!login || seen.has(login)) return;
    seen.add(login);
    out.push(login);
  });
  return out;
};

/**
 * Crew rosters live in the server's custom-lists.json (fetched per handle);
 * the constants above only cover for a crew the server doesn't know or a
 * failed fetch.
 */
export const resolveCrewRoster = (
  id: CrewChannelId,
  rosters: Partial<Record<CrewChannelId, string[]>>,
  channels: Channel[] = CHANNELS,
): string[] => {
  const server = rosters[id];
  if (server && server.length > 0) return normalizeLogins(server);
  if (id === 'npxl') return normalizeLogins(NPXL_FALLBACK_STREAMERS);
  const list = getChannel(id, channels).fallbackStreamers || [];
  return normalizeLogins(list);
};

// ————— The server's catalogue —————

export const NPXL_FALLBACK_STREAMERS: string[] = [
  'xqc', 'pokimane', 'sodapoppin', 'sykkuno', 'lirik', 'lilypichu', 'broxh_',
  'valkyrae', 'zerkaa', 'shxtou', 'esfandtv', 'fuslie', 'brookeab', 'nmplol',
  'vei', 'pokelawls', 'buddha', 'dizzy', 'm0xyy', 'cyr', 'ash', 'anthonyz',
  'willneff', 'uhn', 'kyle', 'koil', 'surefour', 'dasmehdi', 'fanfan', 'blau',
  'angelskimi', 'squeex', 'spaceboy', 'ssaab', 'abdulhd', 'deansocool',
  'roflgator', 'kiwo', 'carmen', 'acie', 'mrmoonshouse', 'sentry', 'bazza',
  'lysium', 'lt_custard', 'miltontpike1', 'omie', 'burn', 'uhsnow', 'crystalst',
  'saiiren', 'geega', 'ashlynn', 'kyliebitkin', 'ziggy', 'sayeed', 'nakkida',
  'curvyllama', 'wakewilder', 'harry', 'disbearex', 'jack', 'lunaoni',
  'mekabear', 'afro', 'ashi', 'travpiper', 'curvyelephant', 'kate', '4head',
  'mikethebard', 'chatterbox', 'nidas', 'middleditch', 'kinamazing', 'tobii',
  'heyimzed', 'kiva', 'vigors', 'ikitty', 'jonthebroski', 'thegeekentry',
  'xiceman', 'pmoney', 'louu', 'jennybeartv', 'rlly', 'chodie', 'cathie',
  'peppo', '52chains', 'capped', 'nikez', 'ladydima', 'nikkisariot', 'pengwin',
  'itsleslie', 'hedisaurus', 'ripoozi', 'chalupa_pants',
];

/** One entry of GET /api/channels */
export interface ServerChannel {
  id: string;
  label: string;
  handle: string;
  room?: string;
  tag?: 'just' | null;
  builtin: boolean;
  streams?: string[];
  createdBy?: string | null;
  updatedAt?: number | null;
}

export const DEFAULT_SERVER_CHANNELS: ServerChannel[] = [
  {
    id: 'npxl',
    label: 'npxl',
    handle: 'npxl',
    room: 'npxl',
    tag: 'just',
    builtin: false,
    streams: NPXL_FALLBACK_STREAMERS,
  },
];

/** Mirrors the server's rules (server/channel-catalog.js) */
export const CHANNEL_ID_PATTERN = /^[a-z0-9_]{2,20}$/;
export const MAX_CHANNEL_LABEL = 24;
/** One Helix streams query — the server caps channel rosters here */
export const MAX_CHANNEL_STREAMS = 100;

/** A label's natural id, the way the server derives it: "Chess Club" -> "chessclub" */
export const suggestChannelId = (label: string): string =>
  String(label ?? '').replace(/\s+/g, '').toLowerCase().replace(/[^a-z0-9_]/g, '');

/**
 * The dial from the server's catalogue: Top 100, the crews (built-ins in
 * their fixed order, created ones after), the Just row likewise, then
 * Following and Custom. Built-ins keep their constants (fallback rosters
 * included); a garbled entry is dropped.
 */
export const mergeServerChannels = (server: ServerChannel[]): Channel[] => {
  const known = new Set(CHANNELS.map(c => c.id));
  const created: Channel[] = [];
  (Array.isArray(server) ? server : []).forEach(entry => {
    if (!entry || typeof entry.id !== 'string' || !CHANNEL_ID_PATTERN.test(entry.id) || known.has(entry.id)) return;
    const label = typeof entry.label === 'string' ? entry.label.trim() : '';
    if (!label) return;
    known.add(entry.id);
    const channel: Channel = {
      id: entry.id,
      label,
      room: entry.id,
      handle: typeof entry.handle === 'string' && entry.handle ? entry.handle : entry.id,
      builtin: false,
    };
    if (entry.tag === 'just') channel.tag = 'just';
    created.push(channel);
  });
  const crews = [
    ...CHANNELS.filter(c => c.handle && c.tag !== 'just'),
    ...created.filter(c => c.tag !== 'just'),
  ];
  const just = [
    ...CHANNELS.filter(c => c.tag === 'just'),
    ...created.filter(c => c.tag === 'just'),
  ];
  return [getChannel('top'), ...crews, ...just, getChannel('followed'), getChannel('custom')];
};

/** Rosters by channel id from the catalogue; empty ones are left out so the constants can stand in */
export const rostersFromServer = (server: ServerChannel[]): Partial<Record<CrewChannelId, string[]>> => {
  const out: Partial<Record<CrewChannelId, string[]>> = {};
  (Array.isArray(server) ? server : []).forEach(entry => {
    if (!entry || typeof entry.id !== 'string' || !Array.isArray(entry.streams)) return;
    const streams = entry.streams.filter((s): s is string => typeof s === 'string');
    if (streams.length > 0) out[entry.id] = streams;
  });
  return out;
};

// The last catalogue this browser saw, so a reload racks created channels
// (and their rosters) straight away instead of after the API answers
const CHANNELS_CACHE_KEY = 'pogchamp_channels';

export const cacheServerChannels = (server: ServerChannel[]): void => {
  try {
    localStorage.setItem(CHANNELS_CACHE_KEY, JSON.stringify(server));
  } catch {
    // storage full or blocked — the next load just waits for the API
  }
};

export const loadCachedServerChannels = (): ServerChannel[] | null => {
  try {
    const raw = localStorage.getItem(CHANNELS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
